import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Components

import BlockWrapper from './BlockWrapper';

// ActionCreators

import { changeData } from '@actions/ssap/ssapActionCreators';


// Utils

import { bitWiseOperatorTools } from '@utils/tools/bitWiseOperatorTools';
import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';
import { normalizeStringToBooleanValue } from '@utils/normalize/normalizeStringToBooleanValue';

// Constants

import { MASKS } from '@constants/ssap/masks/masksConstants';

// Styles

import { darculaFlagsTooltipStyles } from '../darculaTheme';

const {
    rowHoverBackground,
    rowDefaultBackground,
    titleStyle,
    memberHeaderRowStyle,
    memberNameStyle,
    masksToggleStyleBase,
    masksToggleExpandedColor,
    masksToggleCollapsedColor,
    maskTypeRowStyle,
    maskTypeFlagsValueStyle,
    flagDetailRowStyle,
    flagDetailLabelStyle,
    flagDetailValueStyle,
} = darculaFlagsTooltipStyles;

const FlagsTooltip = ({ id }) => {

    const data = useSelector(state => state.ssap.data);
    const householdMembers = useSelector(state => state.ssap.data.taxHousehold.householdMember);
    const dispatch = useDispatch();

    const [ state, setState ] = useState(householdMembers.reduce((acc, hhm, idx) => ({ ...acc, [idx]: false }), {}));

    const getMasksVisibility = members => members.map(() => {
        return Object.keys(MASKS).reduce((acc, maskType) => ({ ...acc, [maskType]: true }), {});
    });

    const [ masksVisibility, toggleMasksVisibility ] = useState(getMasksVisibility(householdMembers));

    useEffect(() => {
        toggleMasksVisibility(getMasksVisibility(householdMembers));
    }, [ householdMembers.length ]);

    const updateTaxHouseholdCompositionFlags = (...args) => {
        const [ type, newState, hhmIndex, updatedFlags ] = args;
        const _thc = payload => {
            const convert = (...payloadInner) => {
                return bitWiseOperatorTools.convertFlagsToValues.apply(this, payloadInner);
            };
            return convert.apply(this, [payload, MASKS[type]]);
        };

        newState
            .taxHousehold
            .householdMember[hhmIndex]
            .taxHouseholdComposition = {
                ...newState
                    .taxHousehold
                    .householdMember[hhmIndex]
                    .taxHouseholdComposition,
                ..._thc(updatedFlags)
            };
    };

    const handleMasksClick = (type, hhmIndex) => {
        const newState = { ...data };

        const newValues = prompt('Enter new value');

        if (newValues === null) {
            return null;
        }

        const updatedFlags = bitWiseOperatorTools
            .setInitialValue(
                MASKS[type],
                newValues
                    .split(',')
                    .map(v => normalizeStringToBooleanValue(v))
            );

        if (type === 'taxHouseholdComposition') {
            updateTaxHouseholdCompositionFlags(type, newState, hhmIndex, updatedFlags);
        }

        newState
            .taxHousehold
            .householdMember[hhmIndex]
            .flagBucket[`${type}Flags`] = updatedFlags;


        dispatch(changeData(newState));
    };

    const handleFlagClick = (type, flagKey, hhmIndex) => {

        const newState = { ...data };

        const updatedFlags = bitWiseOperatorTools
            .getUpdatedFlags(
                householdMembers[hhmIndex].flagBucket[`${type}Flags`],
                [[MASKS[type][flagKey], (v => normalizeStringToBooleanValue(v))(prompt('Enter new value'))]]
            );

        if (type === 'taxHouseholdComposition') {
            updateTaxHouseholdCompositionFlags(type, newState, hhmIndex, updatedFlags);
        }

        newState
            .taxHousehold
            .householdMember[hhmIndex]
            .flagBucket[`${type}Flags`] = updatedFlags;

        dispatch(changeData(newState));
    };

    const handleMemberToggle = idx => {
        setState(prev => ({
            ...prev,
            [idx]: !prev[idx]
        }));
    };

    const handleMasksGroupToggle = (maskType, idx) => {
        toggleMasksVisibility(prev => prev.map((hhm, i) => i !== idx ? hhm : { ...hhm, [maskType]: !hhm[maskType] }));
    };

    return (
        <BlockWrapper id={ id }>
            <div>
                <div style={ titleStyle }>
                    Flags current status
                </div>
                {
                    householdMembers.map((hhm, idx) => {
                        const {
                            flagBucket,
                            name,
                        } = hhm;

                        return (
                            <div
                                key={ `${name}_${idx}` }
                            >
                                <div style={ memberHeaderRowStyle }>
                                    <span
                                        style={ memberNameStyle }
                                        onClick={() => handleMemberToggle(idx)}
                                    >
                                        { normalizeNameOfPerson(name) }
                                    </span>
                                </div>
                                {
                                    Object.entries(MASKS).map(([ maskType, masks ]) => {
                                        const flagsName = `${maskType}Flags`;
                                        return (
                                            <div
                                                key={ maskType }
                                                style={{ display: state[idx] ? 'block' : 'none' }}
                                            >
                                                <div
                                                    style={{
                                                        ...masksToggleStyleBase,
                                                        color: masksVisibility[idx] && masksVisibility[idx][maskType]
                                                            ? masksToggleExpandedColor
                                                            : masksToggleCollapsedColor,
                                                    }}
                                                    onClick={() => handleMasksGroupToggle(maskType, idx)}
                                                >
                                                    [ { masksVisibility[idx] && masksVisibility[idx][maskType] ? 'x' : 'v' } ]
                                                </div>
                                                <span
                                                    style={ maskTypeRowStyle }
                                                    onClick={() => handleMasksClick(maskType, idx)}
                                                >
                                                    {
                                                        maskType
                                                    }
                                                    : flags = &nbsp;
                                                    {
                                                        <span style={ maskTypeFlagsValueStyle }>{ flagBucket && flagBucket[flagsName] || 'No flags' } </span>
                                                    }
                                                </span>
                                                {
                                                    Object.entries(masks).map(indicator => {
                                                        const [ flagName, maskDef ] = indicator;
                                                        const flags = flagBucket && flagBucket[flagsName] || 0;
                                                        return (
                                                            <div
                                                                key={ flagName }
                                                                style={{
                                                                    ...flagDetailRowStyle,
                                                                    display: masksVisibility[idx] && masksVisibility[idx][maskType] ? 'block' : 'none',
                                                                } }
                                                                onMouseEnter={ e => {
                                                                    e.currentTarget.style.backgroundColor = rowHoverBackground;
                                                                } }
                                                                onMouseLeave={ e => {
                                                                    e.currentTarget.style.backgroundColor = rowDefaultBackground;
                                                                } }
                                                            >
                                                                <span style={ flagDetailLabelStyle }>
                                                                    { flagName.split('Flags')[0] }
                                                                </span>
                                                                <span
                                                                    style={ flagDetailValueStyle }
                                                                    onClick={() => handleFlagClick(maskType, flagName, idx)}
                                                                >
                                                                    {
                                                                        (v => String(v !== undefined ? v : 'not got').toLocaleUpperCase()
                                                                        )(bitWiseOperatorTools.getFlagValue(flags)(maskDef))
                                                                    }
                                                                </span>
                                                            </div>
                                                        );
                                                    })
                                                }
                                            </div>
                                        );
                                    })
                                }
                            </div>
                        );
                    })

                }
            </div>
        </BlockWrapper>
    );
};

export default FlagsTooltip;
